import cx from 'classnames';

import type { Masked } from '~/contentful/graphql';
import CardLogoIcon from '~/ui/assets/icons/card-logo.svg';
import {
  AnalyticsCard,
  type AnalyticsContent,
  AnalyticsModule,
  Event,
} from '~/ui/components/analytics';
import baseStyles from '~/ui/components/cards/base/base.module.scss';
import { FullBleedCTAScope, FullBleedCTATrigger } from '~/ui/components/fullBleedCta';
import { Link } from '~/ui/components/link';
import { Image } from '~/ui/modules/image';
import { type Image_AssetFragment } from '~/ui/modules/image/query';
import truncateStyles from '~/ui/styles/truncate.module.scss';
import typographyStyles from '~/ui/styles/typography.module.scss';

import styles from './voices.module.scss';
import { CardLogo } from '../base/CardLogo';

export interface VoicesCardProps {
  title: string;
  href: string;
  isExternal: boolean;
  author?: {
    image?: Masked<typeof Image_AssetFragment>;
    name: string;
    title: string;
    organization: string;
  };
  type?: AnalyticsContent;
  className?: string;
}

export function VoicesCard({ title, href, isExternal, author, type, className }: VoicesCardProps) {
  return (
    <FullBleedCTAScope>
      <article aria-label={title} className={cx(styles.card, className)}>
        {(!author || (!author?.image && isExternal)) && (
          <div className={cx(baseStyles.logoContainer, styles.logoContainer)}>
            <CardLogo randomSeed={title} />
          </div>
        )}
        <div className={cx(styles.eyebrow, typographyStyles.bodySmall)}>Voice</div>
        <div className={cx(styles.title, typographyStyles.bodyLarge, truncateStyles.truncate6)}>
          {title}
        </div>
        <FullBleedCTATrigger>
          <Link
            href={href}
            withIcon
            event={{
              name: Event.CARD_CLICK,
              data: {
                cardModule: AnalyticsModule.RELATED,
                cardType: AnalyticsCard.VOICES,
                cardContentType: type,
                cardCTA: 'Read the essay',
                cardTitle: title,
              },
            }}
            className={styles.link}
          >
            Read the essay
          </Link>
        </FullBleedCTATrigger>
        {author && (
          <div className={cx(styles.author, typographyStyles.caption)}>
            {author.image ? (
              <Image image={author.image} aspectRatio="4/5" className={styles.image} />
            ) : !isExternal ? (
              <div className={styles.imagePlaceholder}>
                <CardLogoIcon className={styles.imagePlaceholderIcon} />
              </div>
            ) : null}
            <div>{author.name}</div>
            <div>{author.title}</div>
            <div>{author.organization}</div>
          </div>
        )}
      </article>
    </FullBleedCTAScope>
  );
}
